/**
 * ProductImageGallery
 * ─────────────────────────────────────────────────────────────────────────
 * THE product media viewer for every web storefront: brand, restaurant,
 * retailer, wholesaler, healthcare and the legacy /product page.
 *
 * Why this file exists in this shape
 * ----------------------------------
 * There used to be TWO galleries, and they had drifted apart. `ZoomGallery`
 * lived inside BrandProductPage.js (and was copy-pasted a second time into
 * Product.jsx, and a third time, hand-rolled, into
 * RetailerWholesalerProductPage.js): it had the Amazon hover lens and the
 * magnified side panel but no render prop, so a caller could never control
 * how its cover rendered. This component had the render prop, the keyboard
 * arrows and the lightbox but no hover zoom, which is why healthcare
 * products looked poorer than brand products for no reason anybody chose.
 * Both are now one component. Anything a product page can do to a picture,
 * every vertical gets, and a change lands in one place instead of four.
 *
 * The single-image fallback is load-bearing
 * -----------------------------------------
 * Most products carry exactly one photograph and no video. In that case
 * this renders the caller's main pane and NOTHING else: no rail, no strip,
 * no counter, no furniture of any kind. A gallery that announces itself
 * around a single picture makes an ordinary product look unfinished, and a
 * pharmacy shelf is mostly single-picture. Do not regress that: the rail
 * appears at two or more media items, never at one.
 *
 * Who owns the main pane
 * ----------------------
 * Callers reach for different cover components: a Cloudinary-optimised
 * contained image on the brand page, the editorial poster hero with its
 * scrim and badges on RestaurantProductDialog. So `renderMain(currentUrl)`
 * is a render prop and the caller decides. Leave it out and the gallery
 * renders its own framed, contained, padded stage, which is what the brand
 * and healthcare pages want. The gallery always owns WHICH item is
 * showing, the rail, the lens, the zoom panel and the lightbox; the caller
 * only ever owns how one still image paints.
 *
 * Image list shape
 * ----------------
 * `images` is a plain array of URL strings, cover at index 0 and extra
 * angles after it. Falsy and duplicate entries are dropped defensively
 * because product fixtures carry empty strings and sometimes repeat the
 * cover inside additionalImages. When the list is empty and a
 * `placeholder` was given, the placeholder stands in as the only image, so
 * a photograph-less product still has a stage.
 *
 * Videos
 * ------
 * `videos` follows the frozen product-video contract (max 3, kind
 * CLOUDINARY or YOUTUBE, see utils/productVideos). Clips render in the rail
 * after the image angles with a play badge and a duration chip; selecting
 * one swaps the stage to an inline player, a YouTube iframe or a native
 * video element. Rows saved before the contract existed are bare Cloudinary
 * URL strings, and the retailer page used to carry its own tolerance for
 * those; that tolerance now lives here so every vertical gets it.
 *
 * Breakpoints belong to the caller
 * --------------------------------
 * The first merge of these galleries hardcoded one set of widths, and that
 * was wrong: each page collapses its own layout at its own width, and three
 * of the originals were tuned to their page. A rail that is still beside
 * the stage after the page has gone single column, or a magnified panel
 * measured against a column that is no longer there, is worse than no
 * gallery change at all. `railBreakpoint`, `stageHeightBreakpoint` and
 * `zoomPanelBreakpoint` are therefore props. Two of them is usually one
 * too many, so `stageHeightBreakpoint` defaults to `railBreakpoint`: brand,
 * healthcare, the restaurant dialog and the retailer page each reason about
 * a single width and pass at most one of the two. Product.jsx is the
 * exception and always was. Its rail flipped at 640 while its stage height
 * flipped at 850 through the `mobile` helper, so it passes 640, 850 and
 * 1100 (that last being where PageGrid drops a column). The
 * retailer/wholesaler page passes 900/1024, where MainLayout collapses and
 * where its info column goes. Anything else that differs per caller belongs
 * here as a prop too, never as a second copy of this component.
 *
 * Zoom, and why there are two kinds
 * ---------------------------------
 * `hoverZoom` turns on the desktop lens: a marquee tracks the pointer over
 * the stage and a panel beside the image column paints the same delivery
 * URL at `zoomScale`. It is suppressed on touch pointers, where a lens
 * glued to a finger is noise. `lightbox` turns on click to enlarge, which
 * is what a phone gets instead. A caller that already has its own
 * magnifier or its own lightbox leaves the matching prop off.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styled from "styled-components";
import ProtectedImage from "./ProtectedImage";
import {
  buildYouTubeEmbedUrl,
  formatVideoDuration,
  getYouTubeId,
  normalizeProductVideos,
} from "../utils/productVideos";
import { getOptimizedUrl } from "../utils/cloudinaryOptimize";

/* Gap between the right edge of the stage and the left edge of the zoom
   panel, and the width the panel takes when the caller gave no `detailRef`
   to measure against. */
const ZOOM_PANEL_GAP = 24;
const ZOOM_PANEL_FALLBACK_WIDTH = 520;
const ZOOM_PANEL_MIN_WIDTH = 80;
const VIEWPORT_MARGIN = 16;

const clamp01 = (n) => Math.max(0, Math.min(1, n));

/* A lens that follows a finger is noise, and on a touch device the
   browser synthesises a single mousemove on tap, which used to flash the
   marquee once and leave it there. Ask the pointer instead of guessing
   from the viewport width. */
const pointerCanHover = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  try {
    return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  } catch (err) {
    return false;
  }
};

const isHttpsUrl = (u) => typeof u === "string" && /^https:\/\//i.test(u);

/* Rows written before the video contract existed come in TWO shapes, and
   normalizeProductVideos rightly refuses both: a bare Cloudinary URL
   string, and an object that carries a `url` but no `kind`. The retailer
   page used to carry its own tolerance for the second one (it read any
   { url } object as a Cloudinary clip unless the row said YOUTUBE) and rows
   saved under that rule are still in the database, so the tolerance has to
   live here or those clips disappear from the page without a word. Promote
   both to contract shape; a row that already declares a kind is handed on
   untouched and the contract validator has the last word either way. */
const toContractVideos = (videos) => {
  if (!Array.isArray(videos)) return [];
  return videos.map((v) => {
    if (isHttpsUrl(v)) {
      return {
        kind: "CLOUDINARY",
        url: v,
        publicId: "",
        thumbnailUrl: "",
        durationSec: 0,
      };
    }
    if (v && typeof v === "object" && !v.kind && isHttpsUrl(v.url)) {
      /* Read the kind off the URL rather than assuming Cloudinary: a
         YouTube link saved before `kind` existed would otherwise mount in a
         <video> element and play nothing. */
      return { ...v, kind: getYouTubeId(v.url) ? "YOUTUBE" : "CLOUDINARY" };
    }
    return v;
  });
};

const PlayGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const ProductImageGallery = ({
  images,
  videos = [],
  alt,
  placeholder,
  renderMain,
  overlay = null,
  detailRef,
  hoverZoom = false,
  lightbox = false,
  zoomScale = 2.5,
  lensSize = 80,
  lensTint = "rgba(255, 255, 255, 0.22)",
  accent = "var(--brand-primary, #324d54)",
  mainHeight = "460px",
  mobileMainHeight,
  stagePadding = "20px",
  thumbFit = "contain",
  videoAutoplay = true,
  /* The widths at which this gallery changes shape. They are NOT one
     number for the whole platform: each page collapses its own layout at
     its own width, and the gallery has to move with the page it sits in.
     `railBreakpoint` is where the side rail turns into a strip under the
     stage. `stageHeightBreakpoint` is where `mobileMainHeight` takes over,
     and defaults to `railBreakpoint` because on most pages those are one
     and the same width; Product.jsx is the page where they genuinely are
     not, and it passes both. `zoomPanelBreakpoint` is where there is no
     longer a column to the right worth magnifying into. The defaults are
     what the brand, healthcare and restaurant surfaces already use. */
  railBreakpoint = 640,
  stageHeightBreakpoint = railBreakpoint,
  zoomPanelBreakpoint = 900,
  hint,
  touchHint,
  className,
}) => {
  /* Drop falsy entries (products carry empty-string placeholders) and
     deduplicate, so a cover repeated inside additionalImages does not buy
     itself a second thumbnail. */
  const list = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const u of Array.isArray(images) ? images : []) {
      if (typeof u !== "string" || !u) continue;
      if (seen.has(u)) continue;
      seen.add(u);
      out.push(u);
    }
    if (!out.length && placeholder) return [placeholder];
    return out;
  }, [images, placeholder]);

  /* Contract-validated clips (max 3, CLOUDINARY or YOUTUBE). Malformed
     records drop rather than render broken. */
  const videoList = useMemo(
    () => normalizeProductVideos(toContractVideos(videos)),
    [videos]
  );

  /* One rail: image angles first, clips after, the order every
     marketplace has taught shoppers to expect. */
  const media = useMemo(
    () => [
      ...list.map((url) => ({ type: "image", url })),
      ...videoList.map((video) => ({ type: "video", video })),
    ],
    [list, videoList]
  );

  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(null); // { x, y } in 0..1 over the stage
  const [panelRect, setPanelRect] = useState(null); // viewport coords
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [canHover] = useState(pointerCanHover);
  const stageRef = useRef(null);

  /* If the media list shrinks (a vendor removed an angle while the page
     was open), pin the selection back to something that exists. */
  useEffect(() => {
    if (active >= media.length && media.length > 0) setActive(0);
  }, [active, media.length]);

  const zoomEnabled = hoverZoom && canHover;

  /* Where the magnified panel goes, or null when it does not fit.
     It starts just right of the stage and runs to the right edge of
     whatever the caller nominated as the detail column, so it fills the
     space the copy occupies instead of a guessed width. Without a
     `detailRef` it takes a sane default and stops short of the viewport
     edge. Null is the honest answer inside a modal: the stage spans the
     dialog and there is no column beside it, so a panel drawn there would
     float outside the dialog over its own dimmed backdrop. */
  const measurePanel = useCallback(
    (rect) => {
      const viewportWidth =
        typeof window !== "undefined" ? window.innerWidth : 0;
      if (viewportWidth && viewportWidth <= zoomPanelBreakpoint) return null;
      const panelLeft = rect.right + ZOOM_PANEL_GAP;
      const boundRight = detailRef?.current
        ? detailRef.current.getBoundingClientRect().right - 8
        : Math.min(
            panelLeft + ZOOM_PANEL_FALLBACK_WIDTH,
            viewportWidth - VIEWPORT_MARGIN
          );
      const width = boundRight - panelLeft;
      if (width <= ZOOM_PANEL_MIN_WIDTH) return null;
      return { left: panelLeft, top: rect.top, width, height: rect.height };
    },
    [detailRef, zoomPanelBreakpoint]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!zoomEnabled || lightboxOpen) return;
      const rect = stageRef.current?.getBoundingClientRect();
      if (!rect) return;
      const panel = measurePanel(rect);
      /* The lens and the panel live or die together. A marquee tracking the
         pointer across a picture that is magnified nowhere is a promise the
         page does not keep, so when there is no room for the panel there is
         no lens either and the stage falls back to click to enlarge. */
      setPanelRect(panel);
      setZoom(
        panel
          ? {
              x: clamp01((e.clientX - rect.left) / rect.width),
              y: clamp01((e.clientY - rect.top) / rect.height),
            }
          : null
      );
    },
    [zoomEnabled, lightboxOpen, measurePanel]
  );

  const handleMouseLeave = useCallback(() => {
    setZoom(null);
    setPanelRect(null);
  }, []);

  /* Opening the enlarged view retires the lens first. ZoomPanel sits at
     z-index 9999 and LightboxRoot at 2000, so a panel still on screen when
     the lightbox opens paints the magnified crop straight over the dark
     overlay and stays there until the pointer happens to move. */
  const openLightbox = useCallback(() => {
    setZoom(null);
    setPanelRect(null);
    setLightboxOpen(true);
  }, []);

  const onKey = useCallback(
    (e) => {
      if (media.length < 2) return;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        e.preventDefault();
        setActive((i) => (i + 1) % media.length);
      } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
        e.preventDefault();
        setActive((i) => (i - 1 + media.length) % media.length);
      }
    },
    [media.length]
  );

  /* Escape closes the enlarged view. Bound only while it is open, so the
     page keeps its own Escape handling the rest of the time, and bound in
     the CAPTURE phase with propagation stopped so the same key press cannot
     also reach a handler further down the tree. Inside a MUI Dialog it
     otherwise closed the picture and the product dialog behind it on one
     press, which reads as the whole thing falling over. */
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onEsc = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setLightboxOpen(false);
    };
    document.addEventListener("keydown", onEsc, true);
    return () => document.removeEventListener("keydown", onEsc, true);
  }, [lightboxOpen]);

  const current = media.length
    ? media[Math.min(active, media.length - 1)]
    : null;

  /* ONE delivery URL for the stage, the zoom panel and the enlarged view:
     the browser downloads it once and reuses it three times, and it is the
     1200px `fullView` derivative with c_limit, never the raw master and
     never a cover-cropped square. */
  const stageSrc =
    current?.type === "image" ? getOptimizedUrl(current.url, "fullView") : null;

  const ownsStage = typeof renderMain !== "function";

  const renderVideo = (video) => {
    if (video.kind === "YOUTUBE") {
      const embedUrl = buildYouTubeEmbedUrl(video.url, {
        autoplay: videoAutoplay,
      });
      if (!embedUrl) return null;
      return (
        <VideoFrame
          key={video.url}
          $height={ownsStage ? mainHeight : null}
          $mobileHeight={mobileMainHeight}
          $stageBp={stageHeightBreakpoint}
        >
          <iframe
            src={embedUrl}
            title={`${alt || "Product"} video`}
            /* `allow` follows the same switch as the src: a frame that is
               still permitted to autoplay can start itself again after the
               query string has said not to. */
            allow={`accelerometer; ${
              videoAutoplay ? "autoplay; " : ""
            }clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share`}
            allowFullScreen
          />
        </VideoFrame>
      );
    }
    return (
      <VideoFrame
        key={video.url}
        $height={ownsStage ? mainHeight : null}
        $mobileHeight={mobileMainHeight}
        $stageBp={stageHeightBreakpoint}
      >
        <video
          src={video.url}
          controls
          playsInline
          preload="metadata"
          poster={video.thumbnailUrl || undefined}
        />
      </VideoFrame>
    );
  };

  /* The gallery's own stage: framed, padded, contained, so nothing is ever
     cropped. Only used when the caller did not supply `renderMain`. */
  const renderDefaultMain = (url) => (
    <StageImage
      $pad={stagePadding}
      src={url ? getOptimizedUrl(url, "fullView") : placeholder}
      alt={alt}
      onError={(e) => {
        if (placeholder && e.target.src !== placeholder) {
          e.target.src = placeholder;
        } else {
          e.target.style.opacity = 0.4;
        }
      }}
    />
  );

  const paintMain = (url) =>
    ownsStage ? renderDefaultMain(url) : renderMain(url);

  const stageHint = canHover ? hint : touchHint || hint;
  const clickable = lightbox && current?.type === "image";

  const stage = current ? (
    current.type === "video" ? (
      renderVideo(current.video)
    ) : (
      <Stage
        ref={stageRef}
        $framed={ownsStage}
        $height={ownsStage ? mainHeight : null}
        $mobileHeight={ownsStage ? mobileMainHeight : null}
        $accent={accent}
        $stageBp={stageHeightBreakpoint}
        /* null, not "default", when the stage does nothing on a click:
           a caller that paints its own copy over the picture (the
           restaurant hero sets the dish name in the scrim) keeps the text
           cursor over that text. */
        $cursor={
          zoom ? "crosshair" : clickable || zoomEnabled ? "zoom-in" : null
        }
        onMouseMove={zoomEnabled ? handleMouseMove : undefined}
        onMouseLeave={zoomEnabled ? handleMouseLeave : undefined}
        onClick={clickable ? openLightbox : undefined}
      >
        {paintMain(current.url)}
        {/* Whatever the caller pins to the stage: the deal stub, a badge. */}
        {overlay}
        {zoom && (
          <Lens
            style={{
              left: `${zoom.x * 100}%`,
              top: `${zoom.y * 100}%`,
              width: typeof lensSize === "number" ? `${lensSize}px` : lensSize,
              height: typeof lensSize === "number" ? `${lensSize}px` : lensSize,
            }}
            $accent={accent}
            $tint={lensTint}
          />
        )}
        {stageHint ? <StageHint>{stageHint}</StageHint> : null}
      </Stage>
    )
  ) : null;

  const zoomPanel =
    current?.type === "image" && zoom && panelRect ? (
      <ZoomPanel
        data-protected-image=""
        $zoomBp={zoomPanelBreakpoint}
        style={{
          position: "fixed",
          left: panelRect.left,
          top: panelRect.top,
          width: panelRect.width,
          height: panelRect.height,
          backgroundImage: `url(${stageSrc})`,
          backgroundSize: `${zoomScale * 100}%`,
          backgroundPosition: `${zoom.x * 100}% ${zoom.y * 100}%`,
        }}
      />
    ) : null;

  const enlarged =
    lightboxOpen && stageSrc ? (
      <LightboxRoot onClick={() => setLightboxOpen(false)}>
        <LightboxClose
          type="button"
          aria-label="Close"
          onClick={() => setLightboxOpen(false)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        </LightboxClose>
        <ProtectedImage
          src={stageSrc}
          alt={alt}
          onClick={(e) => e.stopPropagation()}
        />
      </LightboxRoot>
    ) : null;

  /* Nothing to show and no placeholder to stand in. A caller that paints
     its own cover gets exactly what it would have rendered alone, which for
     the modal callers is nothing. A caller that leans on this component's
     stage keeps the empty frame rather than `null`: this platform lets a
     product go live before its photograph arrives, and returning nothing
     emptied the grid cell the gallery sits in, so the page reflowed around
     a hole until somebody uploaded a picture. Callers that pass a
     `placeholder` never reach here at all. */
  if (media.length === 0) {
    if (!ownsStage) {
      return (
        <SinglePane className={className}>{renderMain(undefined)}</SinglePane>
      );
    }
    return (
      <SinglePane className={className}>
        <Stage
          $framed
          $height={mainHeight}
          $mobileHeight={mobileMainHeight}
          $accent={accent}
          $stageBp={stageHeightBreakpoint}
        >
          {overlay}
        </Stage>
      </SinglePane>
    );
  }

  /* THE SINGLE-IMAGE FALLBACK. One picture and no clip means no rail and no
     chrome, just the pane. See the header: this is deliberate. */
  if (media.length === 1) {
    return (
      <SinglePane className={className}>
        {stage}
        {zoomPanel}
        {enlarged}
      </SinglePane>
    );
  }

  return (
    <Wrap
      className={className}
      onKeyDown={onKey}
      tabIndex={-1}
      $railBp={railBreakpoint}
    >
      <Rail
        role="tablist"
        aria-label={`${alt || "Product"} media`}
        $railBp={railBreakpoint}
      >
        {media.map((item, i) => {
          if (item.type === "image") {
            return (
              <Thumb
                key={item.url}
                type="button"
                role="tab"
                aria-selected={i === active}
                aria-label={`View image ${i + 1} of ${media.length}`}
                $active={i === active}
                $accent={accent}
                $fit={thumbFit}
                /* Hover swaps as well as click: the Amazon detail-page move
                   nobody else copies and everybody subconsciously misses.
                   Click stays mandatory for touch and keyboard. */
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
              >
                <img
                  src={getOptimizedUrl(item.url, "railThumb")}
                  alt=""
                  loading="lazy"
                />
              </Thumb>
            );
          }
          const badge = formatVideoDuration(item.video.durationSec);
          return (
            <Thumb
              key={item.video.url}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Play video ${i + 1} of ${media.length}`}
              $active={i === active}
              $accent={accent}
              $fit={thumbFit}
              /* Click only: hover-to-swap would mount an autoplaying player
                 on an accidental mouse pass. */
              onClick={() => setActive(i)}
            >
              {item.video.thumbnailUrl ? (
                <img src={item.video.thumbnailUrl} alt="" loading="lazy" />
              ) : (
                <VideoThumbFallback />
              )}
              <PlayOverlay>
                <PlayGlyph />
              </PlayOverlay>
              {badge ? <DurationBadge>{badge}</DurationBadge> : null}
            </Thumb>
          );
        })}
      </Rail>

      <MainPane>{stage}</MainPane>
      {zoomPanel}
      {enlarged}
    </Wrap>
  );
};

export default ProductImageGallery;

/* ═══════════════════════════ styles ═══════════════════════════ */

const Wrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: flex-start;
  width: 100%;
  /* The gallery must never be the thing that decides how wide the page is.
     A grid or flex parent sizes its "1fr" column from the min-content of what
     is inside it, and a strip of six 64px thumbnails has a min-content of
     over 400px: on a phone the whole product column, stage and all, grew to
     fit the strip and the page scrolled sideways (brand page, 11 Oct 2026).
     min-width: 0 here and inline-size containment on the strip below stop
     the thumbnails from ever being counted. */
  min-width: 0;
  max-width: 100%;
  outline: none;

  /* column-reverse, so the DOM keeps rail-then-main (correct reading and
     tab order) while the narrow layout shows the picture first and the
     strip underneath it. The width is the caller's, not one number for the
     whole platform: it is where THAT page's own grid collapses, which is
     where a side rail stops fitting beside the stage. */
  @media (max-width: ${(p) => p.$railBp}px) {
    flex-direction: column-reverse;
  }
`;

const SinglePane = styled.div`
  width: 100%;
`;

const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-shrink: 0;
  width: 68px;
  max-height: 520px;
  overflow-y: auto;
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 2px;
  }

  @media (max-width: ${(p) => p.$railBp}px) {
    flex-direction: row;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    padding-bottom: 4px;
    /* Size from the container, never from the thumbnails: the strip scrolls
       inside the width it is given instead of widening the page. */
    contain: inline-size;
  }
`;

const Thumb = styled.button`
  position: relative;
  width: 64px;
  height: 64px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 2px solid ${(p) => (p.$active ? p.$accent : "#e2e8f0")};
  background: #f8fafc;
  padding: 2px;
  cursor: pointer;
  overflow: hidden;
  transition: border-color 120ms ease;
  box-sizing: border-box;

  &:hover {
    border-color: ${(p) => p.$accent};
  }
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(10, 87, 139, 0.25);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: ${(p) => p.$fit};
    display: block;
  }
`;

const PlayOverlay = styled.span`
  position: absolute;
  inset: 2px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.35);
  pointer-events: none;

  svg {
    width: 22px;
    height: 22px;
    fill: #fff;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
  }
`;

const DurationBadge = styled.span`
  position: absolute;
  right: 4px;
  bottom: 4px;
  padding: 1px 4px;
  border-radius: 4px;
  background: rgba(15, 23, 42, 0.75);
  color: #fff;
  font-size: 10px;
  line-height: 1.3;
  pointer-events: none;
`;

const VideoThumbFallback = styled.span`
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  background: #0f172a;
`;

/* `width: 100%` is load-bearing. Below 640px Wrap becomes a column with
   `align-items: flex-start`, so this box is no longer stretched and takes
   its content width. An image stage still gets one from the <img>; a video
   stage's player is absolutely positioned and has none, so the frame
   collapsed to its 1.5px border: a tall hairline and no video, until the
   phone was turned to landscape and the row layout came back. */
const MainPane = styled.div`
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
`;

/* The stage carries the pointer handlers, the lens and anything the caller
   overlays, whether the picture inside it came from renderMain or from this
   component. `$framed` is on only when this component painted it: a caller
   with its own hero brings its own frame and must not get a second one. */
const Stage = styled.div`
  position: relative;
  width: 100%;
  ${(p) => (p.$cursor ? `cursor: ${p.$cursor};` : "")}

  /* Scoped to the picture rather than sitting on the whole stage. A caller
     may paint text over the photograph (the restaurant hero sets the dish
     name and its tagline into the scrim) and that copy has to stay
     selectable; an unselectable product name reads as a broken page. */
  img,
  video {
    user-select: none;
    -webkit-user-drag: none;
  }

  ${(p) =>
    p.$framed
      ? `
    height: ${p.$height};
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    overflow: hidden;
    transition: border-color 0.2s;
    &:hover { border-color: ${p.$accent}; }
  `
      : ""}

  /* The stage's own width, which is not always the rail's. On the legacy
     product page the rail has room to stay vertical beside a stage that has
     already had to shorten. */
  @media (max-width: ${(p) => p.$stageBp}px) {
    ${(p) => (p.$framed && p.$mobileHeight ? `height: ${p.$mobileHeight};` : "")}
  }
`;

const StageImage = styled(ProtectedImage)`
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  padding: ${(p) => p.$pad};
  box-sizing: border-box;
`;

const StageHint = styled.div`
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  color: #999;
  background: rgba(255, 255, 255, 0.85);
  padding: 3px 10px;
  border-radius: 10px;
  pointer-events: none;
  white-space: nowrap;
`;

const Lens = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  border: 2px solid ${(p) => p.$accent};
  border-radius: 4px;
  background: ${(p) => p.$tint};
  pointer-events: none;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6) inset;
`;

const ZoomPanel = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background-color: #fff;
  background-repeat: no-repeat;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
  z-index: 9999;
  pointer-events: none;

  /* Below this there is no column to the right worth painting into. The
     width is the caller's: it is where that page drops the column the panel
     was going to fill. measurePanel applies the same number in JS so the
     lens never outlives the panel; this rule is the belt to that brace. */
  @media (max-width: ${(p) => p.$zoomBp}px) {
    display: none;
  }
`;

/* 16:9 stage for the inline player when the caller owns the cover (a
   modal), the caller's own stage height when this component owns it (a
   product page). A phone is narrower than it is tall, so a fixed stage for
   a 16:9 clip is two thirds black: let the clip set the height there. */
const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  ${(p) => (p.$height ? `height: ${p.$height};` : "aspect-ratio: 16 / 9;")}
  background: #000;
  border: ${(p) => (p.$height ? "1.5px solid #e2e8f0" : "0")};
  border-radius: ${(p) => (p.$height ? "14px" : "8px")};
  overflow: hidden;
  box-sizing: border-box;

  @media (max-width: ${(p) => p.$stageBp}px) {
    height: auto;
    aspect-ratio: 16 / 9;
  }

  iframe,
  video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }

  video {
    object-fit: contain;
  }
`;

const LightboxRoot = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  cursor: zoom-out;

  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    cursor: default;
  }
`;

const LightboxClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.26);
  }

  svg {
    width: 22px;
    height: 22px;
    fill: currentColor;
  }
`;
